import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Res, TicketLineDTO, DiscountMode, SoldProductType } from 'core/api';

import * as Division from './Division';
import { SessionProvider } from 'core/infrastructure';
import { ActionButtonDefinition, TableCompanionsIcon } from 'pos/ui';
import { Col, Cols, ErrorBlock, Flex, Format } from 'core/ui';
import classnames from 'classnames';
import { TicketLineDivision } from './Division';
import { DivisionScreen } from './DivisionScreen';
import { Modal } from 'core/ui-next/modal';

type Props = { initialStatus: Division.TicketDivisionStatus, tableCompanions: Nullable<number> } & Modal.Actions<void>;

type State = Division.TicketDivisionStatus & {
	selectedLine?: TicketLineDivision | 'TableCompanions'
};

const formatQuantity = UI.Format.asDecimalWith(2);

function getLineCashDiscountCode(line: TicketLineDTO): string {
	return line.AppliedDiscountMode === DiscountMode.Cash ? line.AppliedDiscountCode : '';
}

function getLineDiscountPercentageCode(line: TicketLineDTO): string {
	const codes: string[] = [];

	if (line.AppliedOfferCode)
		codes.push(line.AppliedOfferCode);

	if (line.AppliedDiscountMode === DiscountMode.Rate)
		codes.push(line.AppliedDiscountCode);

	return codes.join('/');
}

function DivisionLines(
	{
		leftTableCompanions,
		rightTableCompanions,
		selectedDivision,
		divisions,
		onSelectDivision,
		onTableCompanionsSelect,
		onMoveOneToLeft,
		onMoveOneToRight,
		onMoveOneTableCompanionsToLeft,
		onMoveOneTableCompanionsToRight

	}: {
		leftTableCompanions: Nullable<number>,
		rightTableCompanions: Nullable<number>,
		selectedDivision?: Division.TicketLineDivision | 'TableCompanions',
		divisions: Division.TicketLineDivision[],
		onSelectDivision: (division: Division.TicketLineDivision) => void,
		onTableCompanionsSelect: () => void,
		onMoveOneToRight: (line: TicketLineDTO) => void,
		onMoveOneToLeft: (line: TicketLineDTO) => void,
		onMoveOneTableCompanionsToLeft: () => void,
		onMoveOneTableCompanionsToRight: () => void
	}
) {

	const isTableCompanionLineSelected = selectedDivision === 'TableCompanions';
	const hasTableCompanions = leftTableCompanions || rightTableCompanions;

	const moveTableCompanionsToLeft = () => isTableCompanionLineSelected && onMoveOneTableCompanionsToLeft();
	const moveTableCompanionsToRight = () => isTableCompanionLineSelected && onMoveOneTableCompanionsToRight();

	const lines = divisions.map(d => {

		const addins = d.line.Addins.map(ad => {
			return <span key={ad.SaleFormatId}><br />{Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITH_LBL()} {ad.ProductName} </span>;
		});

		const notes = d.line.Notes.map((note, index) => {
			return <span key={index}><br />*  {note}  *</span>;
		});

		const isLineSelected = isTableCompanionLineSelected ? false : selectedDivision && selectedDivision.line.Id === d.line.Id;

		const isMenuItem = d.line.Product.Type === SoldProductType.MenuItem;

		const cashDiscount = d.line.HasCashDiscount ? <span className='text-muted'><br />{Res.Web.SPLIT_TICKET_LINE_CASH_DISCOUNT_LBL(getLineCashDiscountCode(d.line), d.line.CashDiscount)}</span> : null;
		const percentageDiscount = d.line.HasDiscountPercentage ? <span className='text-muted' style={{ whiteSpace: 'normal' }}><br />{Res.Web.SPLIT_TICKET_LINE_PERCENTAGE_DISCOUNT_LBL(getLineDiscountPercentageCode(d.line), Format.asPercentage(d.line.DiscountPercentage), d.line.DiscountPercentageInCash)}</span> : null;
		const saleFormatName = <span>{d.line.Product.SaleFormatName}</span>;

		const moveOneToLeft = () => isLineSelected && onMoveOneToLeft(d.line);
		const moveOneToRight = () => isLineSelected && onMoveOneToRight(d.line);

		return <li key={d.line.Id} className={classnames({ 'selected': isLineSelected, 'text-muted': isMenuItem })} onClick={() => onSelectDivision(d)}>
			<Cols>
				<Col width='5ex' className='text-right' onClick={moveOneToLeft}>
					{d.leftQty ? formatQuantity(d.leftQty) : ''}
				</Col>
				<Col className='text-center' onClick={moveOneToLeft} width='6ex' style={{ cursor: isLineSelected ? 'pointer' : 'default' }}>
					{isLineSelected ? <Images.Left height='16' width='16' style={{ height: '100%' }} /> : null}
				</Col>
				<Col width='*'>
					{saleFormatName}
					{addins}
					{notes}
					{cashDiscount}
					{percentageDiscount}
				</Col>
				<Col className='text-center' onClick={moveOneToRight} width='6ex' style={{ cursor: isLineSelected ? 'pointer' : 'default' }}>
					{isLineSelected ? <Images.Right height='16' width='16' style={{ height: '100%' }} /> : null}
				</Col>
				<Col width='7ex' className='text-right' onClick={moveOneToRight}>
					{d.rightQty ? formatQuantity(d.rightQty) : ''}
				</Col>
			</Cols>
		</li>;
	});

	return <Flex.Vertical>
		<Flex.Item>
			<Cols className='list-header' >
				<Col width='5ex' className='text-right'>
					{Res.Web.SPLIT_TICKET_SOURCE_QTY()}
				</Col>
				<Col width='6ex'>&nbsp;</Col>
				<Col width='*'>
					{Res.Web.SPLIT_TICKET_PRODUCT()}
				</Col>
				<Col width='6ex'>&nbsp;</Col>
				<Col width='7ex' className='text-right'>
					{Res.Web.SPLIT_TICKET_TARGET_QTY()}
				</Col>
			</Cols>
		</Flex.Item>
		<Flex.Item size='*'>
			<ul className='list'>
				{
					hasTableCompanions ? <li onClick={() => onTableCompanionsSelect()} className={classnames({ 'selected': isTableCompanionLineSelected })}>
						<Cols>

							<Col width='5ex' className='text-right' onClick={moveTableCompanionsToLeft}><TableCompanionsIcon value={leftTableCompanions} /></Col>
							<Col width='6ex' className='text-center' onClick={() => isTableCompanionLineSelected && onMoveOneTableCompanionsToLeft()}> {isTableCompanionLineSelected ? <Images.Left height='16' width='16' style={{ height: '100%' }} /> : null}</Col>
							<Col width='*'><strong>{Res.Web.SPLIT_TICKET_TABLE_COMPANIONS()}</strong></Col>
							<Col width='6ex' className='text-center' onClick={moveTableCompanionsToRight}> {isTableCompanionLineSelected ? <Images.Right height='16' width='16' style={{ height: '100%' }} /> : null}</Col>
							<Col width='7ex' className='text-right' onClick={moveTableCompanionsToRight}><TableCompanionsIcon value={rightTableCompanions} /></Col>
						</Cols>
					</li>
						: null
				}
				{lines}
			</ul>
		</Flex.Item>
	</Flex.Vertical>;
}


export class PhoneOrTabletDivisionScreen extends DivisionScreen<State> {

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const closeButton = SessionProvider.getCurrent().AllowCashTransactions
			? { title: Res.Web.CLOSE_TICKET_BTN(), image: Images.CloseTicket, action: async () => this.setState(await Division.closeTicket(this.state)) }
			: null;

		return [
			this.cancelButton(),
			null,
			null,
			{ title: Res.Web.PRINT_PARK_TICKET_BTN(), image: Images.PrintTicket, action: async () => this.setState(await Division.print(this.state)) },
			closeButton,
			{ title: Res.Web.PRINT_MOVE_TICKET_BTN(), image: Images.MoveTicket, action: async () => this.setState(await Division.move(this.state)) }
		];
	}

	protected getContent(): React.ReactNode {

		const onDivisionSelected = (division: Division.TicketLineDivision) => this.setState({ selectedLine: division });
		const onTableCompanionsSelected = () => this.setState({ selectedLine: 'TableCompanions' });

		const onMoveOneToLeft = async (line: TicketLineDTO) => this.setState(await Division.moveOneToLeft(this.state, line));
		const onMoveOneToRight = async (line: TicketLineDTO) => this.setState(await Division.moveOneToRight(this.state, line));
		const onMoveOneTableCompanionsToRight = async () => this.setState(await Division.moveOneTableCompanionsToRight(this.state));
		const onMoveOneTableCompanionsToLeft = async () => this.setState(await Division.moveOneTableCompanionsToLeft(this.state));

		const hasChashDiscount = this.state.division.Right.CashDiscount !== 0;
		const hasPertentageDiscount = this.props.initialStatus.division.Right.DiscountPercentage !== 0;
		return <Flex.Vertical>

			<Flex.Item>
				<Cols>
					<Col className='m-t-xs m-r-xs m-l-xs text-18 font-bold' width='*'>
						{this.state.mode === 'manual' ? Res.Web.SPLIT_MANUAL_TICKET_LBL() : Res.Web.SPLIT_AUTO_TICKET_LBL(this.state.currentPart + 1, this.state.totalParts)}
					</Col>
				</Cols>
			</Flex.Item>

			{this.state.division.Error ?
				<Flex.Item>
					<Cols>
						<Col className='has-error text-center' width='*'>
							<p><ErrorBlock msg={this.state.division.Error} /></p>
						</Col>
					</Cols>
				</Flex.Item>
				: null
			}

			<Flex.Item size='*'>
				<DivisionLines
					selectedDivision={this.state.selectedLine}
					divisions={this.state.lines}
					onSelectDivision={onDivisionSelected}
					onTableCompanionsSelect={onTableCompanionsSelected}
					onMoveOneToLeft={onMoveOneToLeft}
					onMoveOneToRight={onMoveOneToRight}
					leftTableCompanions={this.state.division.Left.TableCompanions}
					rightTableCompanions={this.state.division.Right.TableCompanions}
					onMoveOneTableCompanionsToLeft={onMoveOneTableCompanionsToLeft}
					onMoveOneTableCompanionsToRight={onMoveOneTableCompanionsToRight}
				/>
			</Flex.Item>

			<Flex.Item className='text-right p-t-xs p-b-xs p-r'>
				<strong>{Res.Web.SPLIT_TICKET_CUSTOMER_LBL()} </strong> {this.props.initialStatus.division.Right.Customer.FiscalName}
			</Flex.Item>
			<Flex.Item className='text-right p-b-xs p-r'>
				{hasPertentageDiscount || hasChashDiscount
					? <>
						<strong>{Res.Web.SPLIT_TICKET_DISCOUNT_LBL()} </strong>
						{hasPertentageDiscount ? `${UI.Format.asPercentage(this.props.initialStatus.division.Right.DiscountPercentage)} (${UI.Format.asMoney(this.state.division.Right.DiscountPercentageInCash)})` : ''}
						{hasChashDiscount ? UI.Format.asMoney(this.state.division.Right.CashDiscount) : ''}
					</>
					: null
				}

			</Flex.Item>
			<Flex.Item className='text-right font-bold p-b-xs p-r text-26'>
				<strong>{Res.Web.SPLIT_TICKET_TOTAL_LBL(this.state.division.Right.NetAmount)}</strong>
			</Flex.Item>

		</Flex.Vertical>;
	}

	createInitialState(props: Props): Nullable<State> {
		return { ...props.initialStatus };
	}
}
